import React, { memo, useMemo } from 'react';
import { CodeScannerInput, Input, Text } from 'v3/components';
import UploadChecklistMedia from 'v3/components/UploadChecklistMedia';
import DeleteOutlineIcon from '@mui/icons-material/DeleteOutline';
import { styles } from '../styles';

import {
  isAnswered,
  hasBackAttachments,
  hasLocalFiles,
  isAttachmentRequiredByBackend,
  shouldRenderUpload,
  isDescriptiveSlug,
  isSelectableSlug,
  isMediaSlug,
  isPlateSlug,
  getInputValue,
} from '../Utils/helper';

const Card = memo(function Card({
  q,
  idx,
  locked,
  errVisible,
  qErrors,
  touchQuestion,
  onToggleUnique,
  onToggleMultiple,
  onSetDescriptive,
  onSetUpload,
  onRemoveFile,
}) {
  const slug = q?.type?.slug;
  const isCodeReader = slug === 'code' || slug === 'code_reader';
  const isPlateQuestion = isPlateSlug(slug);
  const isVehiclePlate = slug === 'vehicle_plate';

  const attachmentRequired = isAttachmentRequiredByBackend(q);
  const showUpload = shouldRenderUpload(q);

  const ok = useMemo(() => {
    return (
      isAnswered(q) &&
      (!attachmentRequired || hasBackAttachments(q) || hasLocalFiles(q))
    );
  }, [q, attachmentRequired]);

  const cardBase =
    typeof styles.questionCard === 'function'
      ? styles.questionCard(ok)
      : styles.questionCard || {};

  const cardStyle = {
    ...cardBase,
    border: errVisible ? '1px solid #FCA5A5' : cardBase.border,
    height: '100%',
    display: 'flex',
    flexDirection: 'column',
  };

  return (
    <div style={cardStyle}>
      <div style={styles.qTop}>
        <div style={styles.qNumber}>
          {typeof idx === 'string' ? idx : idx + 1}
        </div>

        <div style={{ minWidth: 0, width: '100%' }}>
          <div style={styles.qText}>
            {q.question}
            {q.required ? <span style={{ color: '#DC2626' }}> *</span> : null}
          </div>

          <div style={styles.qDivider} />

          {attachmentRequired ? (
            <Text color="gray" style={{ marginTop: 4, display: 'block' }}>
              Responda anexando arquivo(s). (obrigatório)
            </Text>
          ) : showUpload ? (
            <Text color="gray" style={{ marginTop: 4, display: 'block' }}>
              Você pode anexar arquivo(s) se necessário.
            </Text>
          ) : null}
        </div>
      </div>

      {(slug === 'unique' || slug === 'logic') && (
        <div style={{ display: 'flex', gap: 10, width: '100%', marginTop: 12 }}>
          {(q.responses || []).map((r) => (
            <button
              key={r.id}
              type="button"
              style={{
                ...(styles.boolBtn ? styles.boolBtn(!!r.selected) : {}),
                flex: 1,
                width: '100%',
                justifyContent: 'center',
                textAlign: 'center',
                paddingLeft: 10,
                paddingRight: 10,
              }}
              onClick={() => {
                touchQuestion(q.id);
                onToggleUnique(q.id, r.id);
              }}
              disabled={locked}
            >
              {r.description}
            </button>
          ))}
        </div>
      )}

      {slug === 'multiple' && (
        <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
          {(q.responses || []).map((r) => (
            <label
              key={r.id}
              style={{
                display: 'flex',
                alignItems: 'center',
                gap: 10,
                cursor: locked ? 'not-allowed' : 'pointer',
                opacity: locked ? 0.85 : 1,
              }}
            >
              <input
                type="checkbox"
                checked={!!r.selected}
                onChange={() => {
                  touchQuestion(q.id);
                  onToggleMultiple(q.id, r.id);
                }}
                disabled={locked}
              />
              <Text>{r.description}</Text>
            </label>
          ))}
        </div>
      )}

      {isCodeReader && (
        <CodeScannerInput
          label="Código"
          value={q.questionDescriptiveResponse || ''}
          onChange={(value) => {
            touchQuestion(q.id);
            onSetDescriptive(q.id, getInputValue(value));
          }}
          disabled={locked}
        />
      )}

      {isPlateQuestion && (
        <Input
          label={isVehiclePlate ? 'Placa do veículo de tração' : 'Placa do implemento / Carroceria'}
          value={String(
            isVehiclePlate
              ? q?.vehicle?.plate || q?.questionDescriptiveResponse || ''
              : q?.wagon?.plate || q?.questionDescriptiveResponse || ''
          ).toUpperCase()}
          disabled
        />
      )}

      {!isCodeReader &&
        !isPlateQuestion &&
        (isDescriptiveSlug(slug) ||
          (!isSelectableSlug(slug) && !isDescriptiveSlug(slug) && !isMediaSlug(slug))) && (
        <textarea
          value={q.questionDescriptiveResponse || ''}
          onChange={(e) => {
            touchQuestion(q.id);
            onSetDescriptive(q.id, getInputValue(e));
          }}
          placeholder="Digite sua resposta..."
          style={styles.textarea}
          disabled={locked}
        />
      )}

      {showUpload && (
        <div style={{ marginTop: 12 }}>
          <UploadChecklistMedia
            name="file"
            label="Arquivo máximo de 200MB"
            accept=".jpg,.jpeg,.png,.pdf,.mkv,.mp4,.mov"
            multiple
            disabled={locked}
            files={Array.from(q?.attachment?.files || [])}
            onFilesChange={(nextFiles) => {
              touchQuestion(q.id);
              onSetUpload(q.id, nextFiles);
            }}
            onRemove={(index) => {
              touchQuestion(q.id);
              onRemoveFile(q.id, index);
            }}
          />
        </div>
      )}

      {errVisible && (
        <div style={{ marginTop: 10, color: '#DC2626', fontSize: 12, fontWeight: 800 }}>
          * {qErrors?.[q.id]}
        </div>
      )}
    </div>
  );
});

export default Card;
